import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, ChevronLeft, MapPin, User, Utensils, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuthStore } from '@/stores/auth-store';
import { api } from '@/lib/api';
import { DIETARY_RESTRICTIONS, CUISINE_TYPES } from '@rango/shared/utils';
import { cn } from '@/lib/utils';

type Step = 'profile' | 'preferences' | 'location' | 'kyc';

const steps: { id: Step; title: string; icon: typeof User }[] = [
  { id: 'profile', title: 'Perfil', icon: User },
  { id: 'preferences', title: 'Preferências', icon: Utensils },
  { id: 'location', title: 'Localização', icon: MapPin },
  { id: 'kyc', title: 'Verificação', icon: Shield },
];

export function OnboardingPage() {
  const navigate = useNavigate();
  const { user, setUser, mockLogin } = useAuthStore();
  const [currentStep, setCurrentStep] = useState<Step>(user?.onboarding.currentStep as Step || 'profile');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Form state
  const [name, setName] = useState(user?.name || '');
  const [phone, setPhone] = useState(user?.phone || '');
  const [email, setEmail] = useState('');
  const [selectedDietary, setSelectedDietary] = useState<string[]>(user?.preferences.dietaryRestrictions || []);
  const [selectedCuisines, setSelectedCuisines] = useState<string[]>(user?.preferences.favoriteCuisines || []);
  const [addressLine, setAddressLine] = useState(user?.location?.addressLine || '');
  const [city, setCity] = useState(user?.location?.city || '');
  const [state, setState] = useState(user?.location?.state || '');
  const [cpf, setCpf] = useState('');
  const [birthDate, setBirthDate] = useState('');
  
  const currentStepIndex = steps.findIndex(s => s.id === currentStep);
  
  const handleProfileSubmit = async () => {
    if (!name.trim()) {
      setError('Por favor, insira seu nome');
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      // In development, mock login
      if (import.meta.env.DEV && !user) {
        await mockLogin(email || `${name.toLowerCase().replace(/\s/g, '')}@example.com`, name);
      } else if (user) {
        const { user: updatedUser } = await api.updateMe({ name, phone: phone || undefined });
        setUser(updatedUser);
      }
      
      setCurrentStep('preferences');
    } catch (err) {
      setError('Erro ao salvar perfil. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handlePreferencesSubmit = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const { user: updatedUser } = await api.updatePreferences({
        dietaryRestrictions: selectedDietary,
        favoriteCuisines: selectedCuisines,
      });
      setUser(updatedUser);
      setCurrentStep('location');
    } catch (err) {
      setError('Erro ao salvar preferências. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleLocationSubmit = async () => {
    if (!addressLine.trim() || !city.trim() || !state.trim()) {
      setError('Por favor, preencha todos os campos de endereço');
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      // For now, use hardcoded coordinates (São Paulo center)
      // In production, we'd use a geocoding API
      const { user: updatedUser } = await api.updateLocation({
        addressLine,
        city,
        state,
        latitude: -23.5505,
        longitude: -46.6333,
      });
      setUser(updatedUser);
      setCurrentStep('kyc');
    } catch (err) {
      setError('Erro ao salvar localização. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleKycSubmit = async () => {
    if (!cpf.trim() || cpf.replace(/\D/g, '').length !== 11) {
      setError('Por favor, insira um CPF válido');
      return;
    }
    
    if (!birthDate) {
      setError('Por favor, insira sua data de nascimento');
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      const { user: updatedUser } = await api.submitKyc({
        cpf: cpf.replace(/\D/g, ''),
        fullName: name,
        birthDate,
        documentType: 'rg',
        documentNumber: '123456789', // Mock
      });
      setUser(updatedUser);
      navigate('/');
    } catch (err) {
      setError('Erro ao verificar identidade. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleBack = () => {
    const prevIndex = currentStepIndex - 1;
    if (prevIndex >= 0) {
      setCurrentStep(steps[prevIndex].id);
    }
  };
  
  const toggleSelection = (item: string, selected: string[], setSelected: (items: string[]) => void) => {
    if (selected.includes(item)) {
      setSelected(selected.filter(i => i !== item));
    } else {
      setSelected([...selected, item]);
    }
  };
  
  return (
    <div className="w-full max-w-md">
      {/* Progress indicator */}
      <div className="mb-6 flex items-center justify-center gap-2">
        {steps.map((step, index) => {
          const Icon = step.icon;
          const isActive = index === currentStepIndex;
          const isCompleted = index < currentStepIndex;
          
          return (
            <div key={step.id} className="flex items-center">
              <div
                className={cn(
                  'flex h-10 w-10 items-center justify-center rounded-full border-2 transition-colors',
                  isActive && 'border-primary bg-primary text-primary-foreground',
                  isCompleted && 'border-primary bg-primary/10 text-primary',
                  !isActive && !isCompleted && 'border-muted bg-muted text-muted-foreground'
                )}
              >
                <Icon className="h-5 w-5" />
              </div>
              {index < steps.length - 1 && (
                <div
                  className={cn(
                    'mx-1 h-0.5 w-8',
                    isCompleted ? 'bg-primary' : 'bg-muted'
                  )}
                />
              )}
            </div>
          );
        })}
      </div>
      
      <Card className="border-0 bg-white/80 shadow-xl backdrop-blur">
        <CardHeader>
          <CardTitle>{steps[currentStepIndex].title}</CardTitle>
          <CardDescription>
            {currentStep === 'profile' && 'Conte-nos um pouco sobre você'}
            {currentStep === 'preferences' && 'Suas preferências alimentares'}
            {currentStep === 'location' && 'Onde você está localizado?'}
            {currentStep === 'kyc' && 'Verificação de identidade'}
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-4">
          {error && (
            <div className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
              {error}
            </div>
          )}
          
          {/* Profile Step */}
          {currentStep === 'profile' && (
            <>
              <div className="space-y-2">
                <Label htmlFor="name">Nome completo</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Seu nome"
                />
              </div>
              
              {import.meta.env.DEV && !user && (
                <div className="space-y-2">
                  <Label htmlFor="email">E-mail (desenvolvimento)</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="seu@email.com"
                  />
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="phone">Telefone (opcional)</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="(11) 99999-9999"
                />
              </div>
              
              <Button
                onClick={handleProfileSubmit}
                className="w-full"
                disabled={isLoading}
              >
                {isLoading ? 'Salvando...' : 'Continuar'}
                <ChevronRight className="h-4 w-4" />
              </Button>
            </>
          )}
          
          {/* Preferences Step */}
          {currentStep === 'preferences' && (
            <>
              <div className="space-y-2">
                <Label>Restrições alimentares</Label>
                <div className="flex flex-wrap gap-2">
                  {DIETARY_RESTRICTIONS.map((item) => (
                    <button
                      key={item}
                      onClick={() => toggleSelection(item, selectedDietary, setSelectedDietary)}
                      className={cn(
                        'rounded-full border px-3 py-1 text-sm transition-colors',
                        selectedDietary.includes(item)
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'border-input hover:bg-accent'
                      )}
                    >
                      {item}
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Culinárias favoritas</Label>
                <div className="flex flex-wrap gap-2">
                  {CUISINE_TYPES.map((item) => (
                    <button
                      key={item}
                      onClick={() => toggleSelection(item, selectedCuisines, setSelectedCuisines)}
                      className={cn(
                        'rounded-full border px-3 py-1 text-sm transition-colors',
                        selectedCuisines.includes(item)
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'border-input hover:bg-accent'
                      )}
                    >
                      {item}
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="flex gap-2">
                <Button variant="outline" onClick={handleBack}>
                  <ChevronLeft className="h-4 w-4" />
                  Voltar
                </Button>
                <Button
                  onClick={handlePreferencesSubmit}
                  className="flex-1"
                  disabled={isLoading}
                >
                  {isLoading ? 'Salvando...' : 'Continuar'}
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </>
          )}
          
          {/* Location Step */}
          {currentStep === 'location' && (
            <>
              <div className="space-y-2">
                <Label htmlFor="address">Endereço</Label>
                <Input
                  id="address"
                  value={addressLine}
                  onChange={(e) => setAddressLine(e.target.value)}
                  placeholder="Rua, número"
                />
              </div>
              
              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-2 space-y-2">
                  <Label htmlFor="city">Cidade</Label>
                  <Input
                    id="city"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder="São Paulo"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="state">Estado</Label>
                  <Input
                    id="state"
                    value={state}
                    onChange={(e) => setState(e.target.value.toUpperCase().slice(0, 2))}
                    placeholder="SP"
                    maxLength={2}
                  />
                </div>
              </div>
              
              <div className="flex gap-2">
                <Button variant="outline" onClick={handleBack}>
                  <ChevronLeft className="h-4 w-4" />
                  Voltar
                </Button>
                <Button
                  onClick={handleLocationSubmit}
                  className="flex-1"
                  disabled={isLoading}
                >
                  {isLoading ? 'Salvando...' : 'Continuar'}
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </>
          )}
          
          {/* KYC Step */}
          {currentStep === 'kyc' && (
            <>
              <div className="rounded-lg bg-muted p-4 text-sm text-muted-foreground">
                <p className="font-medium text-foreground">Por que pedimos isso?</p>
                <p className="mt-1">
                  Para garantir a segurança da comunidade, verificamos a identidade de todos os usuários.
                  Seus dados são protegidos e nunca compartilhados.
                </p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="cpf">CPF</Label>
                <Input
                  id="cpf"
                  value={cpf}
                  onChange={(e) => setCpf(e.target.value)}
                  placeholder="000.000.000-00"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="birthDate">Data de nascimento</Label>
                <Input
                  id="birthDate"
                  type="date"
                  value={birthDate}
                  onChange={(e) => setBirthDate(e.target.value)}
                />
              </div>
              
              <div className="flex gap-2">
                <Button variant="outline" onClick={handleBack}>
                  <ChevronLeft className="h-4 w-4" />
                  Voltar
                </Button>
                <Button
                  onClick={handleKycSubmit}
                  className="flex-1"
                  disabled={isLoading}
                >
                  {isLoading ? 'Verificando...' : 'Finalizar cadastro'}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

